import { memo } from 'react';
import type { AriaAttributes } from 'react';
import { clsx } from 'clsx';
import { Text } from '@library';
import BaseButton from '../buttons/BaseButton';
import styles from './PaginationButton.module.scss';
import NextIcon from './NextIcon';
import PreviousIcon from './PreviousIcon';

interface PaginationButtonProps extends AriaAttributes {
    /**
     * Current state of pagination component
     */
    page: number;
    /**
     * Page number that this button represents
     */
    value: number;
    /**
     * Use for navigating to next page
     */
    next?: boolean;
    /**
     * Use for navigating to previous page
     */
    previous?: boolean;
    /**
     * Disabled button
     */
    disabled?: boolean;
    onChange(value: number): void;
}

const getLabel = (next: boolean, previous: boolean, value: number) => {
    if (next) {
        return 'next page';
    } else if (previous) {
        return 'previous page';
    }

    return `${value}`;
};

const PaginationButton = ({
    page,
    value,
    onChange,
    next = false,
    previous = false,
    disabled,
}: PaginationButtonProps) => {
    const label = getLabel(next, previous, value);

    return (
        <div className={styles.paginationButton}>
            <BaseButton
                tertiary={page !== value || disabled}
                outline={disabled}
                disabled={disabled}
                onClick={() => onChange(value)}
                theme={styles.buttonTheme}
                aria-label={label}
                data-test-class="paginationPageButton"
                data-test-state={page === value ? 'selected' : undefined}
                component={<button />}
            >
                <div
                    className={clsx(styles.paginationButtonContent, {
                        [styles.topPadded]: next || previous,
                        [styles.leftPadded]: next,
                        [styles.rightPadded]: previous,
                    })}
                >
                    {next && <NextIcon />}
                    {previous && <PreviousIcon />}
                    {!next && !previous && (
                        <Text secondary bold={page === value} uppercase>
                            {value}
                        </Text>
                    )}
                </div>
            </BaseButton>
        </div>
    );
};

export default memo(PaginationButton);
